import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useLocation } from "@/api/hooks/locations";
import { useSite } from "@/api/hooks/sites";
import { useLocationParticipations } from "@/api/hooks/participations";
import DocumentsSection from "@/components/documents/DocumentsSection";
import { formatDate, formatDateTime } from "@/utils/format";

function LabeledValue({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
}

export default function LocationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: location, isLoading, error } = useLocation(id);
  // Fetch the parent site for breadcrumb / back-nav context.
  const { data: site } = useSite(location?.site_id);
  const { data: participations } = useLocationParticipations(location?.id);

  if (isLoading) return <CircularProgress />;
  if (error || !location)
    return <Alert severity="error">{error?.message ?? "Location not found."}</Alert>;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton
          onClick={() => navigate(`/clinops/sites/${location.site_id}`)}
          aria-label="Back to site"
        >
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {site ? (
              <Link to={`/clinops/sites/${site.id}`} style={{ color: "inherit" }}>
                {site.common_name}
              </Link>
            ) : (
              "Location"
            )}
          </Typography>
          <Typography variant="h5">
            <Box component="span" sx={{ fontFamily: "ui-monospace, Menlo, monospace", mr: 1 }}>
              {location.display_id}
            </Box>
            {location.name}
            {!location.is_active && (
              <Chip
                label="Inactive"
                size="small"
                color="default"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "grid", gap: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              Address
            </Typography>
            <Grid container spacing={3} sx={{ mt: 0 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <LabeledValue label="Street">{location.street ?? "—"}</LabeledValue>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <LabeledValue label="City">{location.city ?? "—"}</LabeledValue>
              </Grid>
              <Grid size={{ xs: 3, md: 1.5 }}>
                <LabeledValue label="State">{location.state ?? "—"}</LabeledValue>
              </Grid>
              <Grid size={{ xs: 3, md: 1.5 }}>
                <LabeledValue label="ZIP">{location.postal_code ?? "—"}</LabeledValue>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <LabeledValue label="Created">
                  {formatDateTime(location.created_at)}
                </LabeledValue>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <LabeledValue label="Updated">
                  {formatDateTime(location.updated_at)}
                </LabeledValue>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="overline" color="text.secondary">
                Participations at this Location
              </Typography>
              <Chip
                size="small"
                label={`${participations?.length ?? 0} total`}
              />
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Study</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Phase</TableCell>
                    <TableCell>Activated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(participations ?? []).map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell>
                        <Link to={`/clinops/sites/participations/${p.id}`}>
                          {p.study_name}
                        </Link>
                      </TableCell>
                      <TableCell>{p.status.replace(/_/g, " ")}</TableCell>
                      <TableCell>{p.current_phase} of 4</TableCell>
                      <TableCell>
                        {p.activated_at ? formatDate(p.activated_at) : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(participations ?? []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary" }}>
                        No participations at this location yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <DocumentsSection entityType="location" entityId={location.id} />
      </Box>
    </Box>
  );
}
